import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api, { avatarSrc, formatApiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { VerifiedBadge } from "./VerifiedBadge";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger,
} from "./ui/dropdown-menu";
import { MoreHorizontal, Flag, Ban, X, Heart, Repeat2 } from "lucide-react";
import { toast } from "sonner";

function UserRow({ u }) {
  return (
    <Link to={`/u/${u.username}`} className="flex items-center gap-2.5 py-1.5 group">
      {u.avatar_url ? (
        <img src={avatarSrc(u.avatar_url)} alt="" className="w-8 h-8 rounded-full object-cover bg-[#1B2437]" />
      ) : (
        <div className="w-8 h-8 rounded-full bg-[#0066FF] flex items-center justify-center text-white text-xs font-semibold">{(u.display_name || u.username)?.[0]?.toUpperCase()}</div>
      )}
      <span className="min-w-0">
        <span className="flex items-center gap-1 text-white text-sm font-semibold group-hover:underline"><span className="truncate">{u.display_name || u.username}</span><VerifiedBadge show={u.verified} size={12} /></span>
        <span className="block text-[#8A8D91] text-xs truncate">@{u.username}</span>
      </span>
    </Link>
  );
}

export function NotesModal({ mediaId, open, onClose }) {
  const [notes, setNotes] = useState(null);
  useEffect(() => {
    if (!open) return;
    setNotes(null);
    api.get(`/media/${mediaId}/notes`).then(({ data }) => setNotes(data)).catch(() => setNotes({ likes: [], reblogs: [], comments_count: 0 }));
  }, [open, mediaId]);
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[120] flex items-end sm:items-center justify-center p-0 sm:p-4" data-testid="notes-modal">
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full sm:max-w-md bg-[#182132] border border-white/15 rounded-t-2xl sm:rounded-2xl p-5 max-h-[75vh] overflow-y-auto custom-scrollbar">
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-white font-heading text-lg">Notes</h3>
          <button onClick={onClose} data-testid="notes-close" className="text-[#94A3B8] hover:text-white"><X size={18} /></button>
        </div>
        {!notes ? (
          <p className="text-[#64748B] text-sm py-6 text-center">Loading…</p>
        ) : (
          <>
            {notes.reblogs?.length > 0 && (
              <div className="mb-4">
                <p className="flex items-center gap-1.5 text-[#00E676] text-xs font-mono uppercase tracking-wider mb-1"><Repeat2 size={13} /> {notes.reblogs.length} reblog{notes.reblogs.length === 1 ? "" : "s"}</p>
                {notes.reblogs.map((u) => <UserRow key={`rb${u.id}`} u={u} />)}
              </div>
            )}
            <div>
              <p className="flex items-center gap-1.5 text-[#FF2A5F] text-xs font-mono uppercase tracking-wider mb-1"><Heart size={13} /> {notes.likes.length} like{notes.likes.length === 1 ? "" : "s"}</p>
              {notes.likes.length === 0 && notes.reblogs?.length === 0 ? (
                <p className="text-[#64748B] text-sm py-4 text-center" data-testid="notes-empty">No notes yet — be the first.</p>
              ) : notes.likes.map((u) => <UserRow key={`lk${u.id}`} u={u} />)}
            </div>
          </>
        )}
      </div>
    </div>
  );
}

export function ReblogButton({ item, onChange, className = "", testid }) {
  const { user } = useAuth();
  const [busy, setBusy] = useState(false);
  const toggle = async () => {
    if (!user) return (window.location.href = "/login");
    setBusy(true);
    try {
      const { data } = await api.post(`/media/${item.id}/reblog`, {});
      onChange?.({ reblogged: data.reblogged, reblogs_count: data.reblogs_count });
      toast.success(data.reblogged ? "Reblogged to your blog" : "Reblog removed");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };
  return (
    <button data-testid={testid || `post-reblog-${item.id}`} onClick={toggle} disabled={busy}
      className={`${className} ${item.reblogged ? "text-[#00E676]" : "text-[#A1A1AA]"} disabled:opacity-60`}>
      <Repeat2 size={19} /> Reblog
    </button>
  );
}

export function PostMenu({ item, testid }) {
  const { user } = useAuth();
  const [reporting, setReporting] = useState(false);
  const [reason, setReason] = useState("");
  const isOwn = user && (user.id === item.author_id || user.username === item.author_username);

  const submitReport = async () => {
    if (reason.trim().length < 3) return toast.error("Tell us what's wrong");
    try {
      await api.post("/report", { target_type: "media", target_id: item.id, reason: reason.trim() });
      toast.success("Report sent to moderators");
      setReporting(false); setReason("");
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  const block = async () => {
    if (!user) return (window.location.href = "/login");
    try {
      const { data } = await api.post(`/users/${item.author_id}/block`);
      toast.success(data.blocked ? `Blocked @${item.author_username}` : `Unblocked @${item.author_username}`);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger data-testid={testid || `post-menu-${item.id}`} className="w-9 h-9 rounded-full flex items-center justify-center text-[#94A3B8] hover:bg-white/10 hover:text-white outline-none" aria-label="More">
          <MoreHorizontal size={18} />
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="bg-[#182132] border-white/10 text-[#F1F5F9] w-48">
          <DropdownMenuItem data-testid={`report-post-${item.id}`} onClick={() => { if (!user) { window.location.href = "/login"; return; } setReporting(true); }} className="cursor-pointer focus:bg-white/[0.06]"><Flag size={15} className="mr-2" /> Report</DropdownMenuItem>
          {!isOwn && <DropdownMenuItem data-testid={`block-author-${item.id}`} onClick={block} className="cursor-pointer focus:bg-white/[0.06] text-[#FF6B6B]"><Ban size={15} className="mr-2" /> Block @{item.author_username}</DropdownMenuItem>}
        </DropdownMenuContent>
      </DropdownMenu>
      {reporting && (
        <div className="fixed inset-0 z-[130] flex items-center justify-center p-4" data-testid="report-dialog">
          <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={() => setReporting(false)} />
          <div className="relative w-full max-w-md bg-[#182132] border border-white/15 rounded-2xl p-5 space-y-3">
            <h3 className="text-white font-heading text-lg">Report this post</h3>
            <textarea data-testid="report-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={3} maxLength={600} placeholder="What's wrong with this post?" className="w-full bg-[#111827] border border-white/10 rounded-lg px-3 py-2 text-sm text-white placeholder:text-[#64748B] focus:outline-none focus:border-[#0066FF]" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setReporting(false)} className="px-4 py-2 rounded-lg text-[#94A3B8] hover:text-white text-sm">Cancel</button>
              <button data-testid="report-submit" onClick={submitReport} className="px-4 py-2 rounded-lg bg-[#0066FF] hover:bg-[#0052CC] text-white font-semibold text-sm">Send report</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
}
